import { GroupNodeModel } from "@/models/nodes/GroupNodeModel";
import { OperatorNodeModel } from "@/models/nodes/OperatorNodeModel";
import { OperatorType } from "@/models/nodes/operators";
import { WrapperNodeModel } from "@/models/nodes/WrapperNodeModel";
import { isSupSubNode } from "@/models/utils/semanticChecks";

import { FunctionNodeModel } from "../FunctionNodeModel";
import { InlineNodeModel } from "../InlineNodeModel";

import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeModel } from "@/models/nodes/types";
import type { NodeVisitor } from "@/models/nodes/visitors/NodeVisitor";

export class DeleteNodeVisitor implements NodeVisitor<void> {
  private doc: DocumentModel;

  private index?: number;

  constructor(node: NodeModel, index: number | undefined = undefined) {
    this.index = index;
    this.doc = node.doc;
  }

  private removeNode(node: NodeModel) {
    const { doc } = this;

    if (node.parent?.is(WrapperNodeModel)) {
      this.visitWrapperNode(node.parent);
    }
    // Remove it with it's parent if the parent is unary operator
    else if (node.parent?.is(OperatorNodeModel) && node.parent.arity === "unary") {
      node.parent.accept(this);
    }
    // The group has only one child. If we want to remove it, remove the whold group.
    else if (node.parent?.is(GroupNodeModel)) {
      node.parent.accept(this);
    } else if (
      node.parent?.isInlineEditable() &&
      node.parent?.is(FunctionNodeModel) &&
      node.parent.powerPlacement === "child"
    ) {
      doc.removeNode(node);
      doc.input.editNode(node.parent, node.parent.value.length);
    }
    // Remove the node, for all other cases
    else {
      // Walk up to find a node whose parent is an InlineNodeModel,
      // since moveBeforeNode requires that.
      let target: NodeModel = node;
      while (target.parent && !target.parent.is(InlineNodeModel)) {
        target = target.parent;
      }
      doc.cursor.moveBeforeNode(target);
      doc.removeNode(node);
    }
  }

  private removeParameter(parameters: InlineNodeModel[], indexShift = 0) {
    if (this.index === undefined) {
      return;
    }

    const index = this.index + indexShift;
    const nextNode = parameters[index - 1];
    const prevNode = parameters[index - 2];

    if (prevNode && nextNode) {
      const inlineNodes = [...prevNode.nodes, ...nextNode.nodes];

      this.doc.cursor.moveLeft();
      this.doc.removeNode(nextNode);
      inlineNodes.forEach((n) => prevNode.appendChild(n));
    }
  }

  visitGroupNode(node: NodeModel<"group">): void {
    const { doc } = this;
    doc.cursor.moveBeforeNode(node);
    doc.removeNode(node);
  }

  visitLabelNode(node: NodeModel<"label">): void {
    if (node.isEmpty) {
      let owner: NodeModel = node;
      while (owner.parent && !owner.parent.is(InlineNodeModel)) {
        owner = owner.parent;
      }
      this.doc.removeNode(node);
      this.doc.cursor.moveAfterNode(owner);
    }
  }

  visitSymbolNode(node: NodeModel<"symbol">): void {
    throw new Error(`DeleteNodeVisitor is not implemented for ${node.type}`);
  }

  visitOperatorNode(node: NodeModel<"operator">): void {
    const { doc } = this;

    const isFunction = node.placement === "function";
    const isEmptySubSup = isSupSubNode(node) && node.parameters[0]?.isEmpty;
    const isPostifxOperator = node.placement === "postfix";

    // The cursor is in the end of the node
    // or at the beginning of selected function node, for example {sin(|x+y)},
    // or at the beginning of an empty sub/sup operator
    if (
      this.index === undefined ||
      (this.index === 0 && (isFunction || isEmptySubSup || isPostifxOperator))
    ) {
      this.removeNode(node);
    }

    // FIXME: this description is probably outdated (at least for root operator)
    // The cursor is in the middle of the operator between nodes, remove the operator and replace it with multiply
    // We take the last node from the left to the operator we want to remove, and the firt node from
    // the right. Then combine them in multiplication. For example: a+b=c+d => a+c*d+d
    else if (this.index > 0) {
      // TODO: remove explicit checking for OperatorType.ROOT
      if (node.is(OperatorType.ROOT)) {
        const bodyNode = node.parameters[this.index];
        doc.cursor.moveBeforeNode(node);
        bodyNode.nodes.forEach((n) => node.insertSiblingBefore(n));
        doc.removeNode(node);
      } else {
        const nextNode = node.parameters[this.index];
        const prevNode = node.parameters[this.index - 1];
        if (prevNode && nextNode) {
          const lastPrevNode = prevNode.nodes[prevNode.nodes.length - 1];
          const inlineNodes = [...prevNode.nodes, ...nextNode.nodes];
          inlineNodes.forEach((n) => node.insertSiblingBefore(n));
          doc.removeNode(node);
          doc.cursor.moveAfterNode(lastPrevNode);
        }
      }
    }
    // We want to remove the prefix operators (such as minus), remove the operator but keep it's operand.
    else if (node.placement === "prefix") {
      const { parent } = node;
      if (doc.cursor.isCurrent(node, this.index)) {
        doc.cursor.moveLeft();
      }
      const childNode = node.parameters[0];
      this.index = node.index;
      node.replaceWith(childNode);
      parent?.accept(this);
    }
  }

  visitOperatorSymbolNode(node: NodeModel<"operatorSymbol">): void {
    this.removeNode(node);
  }

  visitNumberNode(node: NodeModel<"number">): void {
    this.removeNode(node);
  }

  visitVariableNode(node: NodeModel<"variable">): void {
    if (this.index === undefined || this.index === 1) {
      const firstParam = node.parameters[0];
      if (node.parameters.length === 1 && firstParam.isEmpty) {
        if (this.doc.cursor.isOnNode(firstParam)) {
          this.doc.cursor.moveAfterNode(node);
        }
        this.doc.removeNode(firstParam);
      } else {
        this.removeNode(node);
      }
    } else if (this.index > 1) {
      this.removeParameter(node.parameters, node.power ? -1 : 0);
    }
  }

  visitTextNode(node: NodeModel<"text">): void {
    this.removeNode(node);
  }

  visitConstantNode(node: NodeModel<"constant">): void {
    this.removeNode(node);
  }

  visitFunctionNode(node: NodeModel<"function">): void {
    if (this.index === undefined || this.index === 1) {
      if (this.doc.cursor.isOnNode(node)) {
        this.doc.cursor.moveBeforeNode(node);
      }
      this.doc.removeNode(node);
    } else if (this.index > 1) {
      this.removeParameter(node.parameters, node.power ? -1 : 0);
    }
  }

  visitInlineNode(node: NodeModel<"inline">): void {
    if (this.index && this.index > 0) {
      const nodeBeforeCursor = node.nodes[this.index - 1];
      this.doc.removeNode(nodeBeforeCursor);
      this.doc.cursor.moveTo(node, this.index - 1);
    } else if (this.index === 0 && node.parent) {
      this.index = node.index;
      node.parent.accept(this);
    }
  }

  visitWrapperNode(node: NodeModel<"wrapper">): void {
    if (!node.isEmpty) {
      return;
    }
    this.doc.removeNode(node);
  }

  visitPlainTextNode(node: NodeModel<"plainText">): void {
    this.removeNode(node);
  }

  visitMatrixNode(node: NodeModel<"matrix">): void {
    this.removeNode(node);
  }

  visitDecoratorNode(node: NodeModel<"decorator">): void {
    this.removeNode(node);
  }

  visitUnitNode(node: NodeModel<"unit">): void {
    if (this.doc.cursor.isOnNode(node)) {
      if (node.placement === "postfix") {
        this.doc.cursor.moveAfterNode(node.parent);
      } else {
        this.doc.cursor.moveBeforeNode(node.parent);
      }
    }

    this.doc.removeNode(node);
  }

  visitGraphNode(node: NodeModel<"graph">): void {
    this.removeNode(node);
  }

  visitGraphExpressionNode(node: NodeModel<"graphExpression">): void {
    this.removeNode(node);
  }

  visitPlaceholderNode(node: NodeModel<"placeholder">): void {
    this.removeNode(node);
  }

  visitComputedReferenceNode(node: NodeModel<"computedReference">): void {
    this.removeNode(node);
  }

  visitComputedResultNode(node: NodeModel<"computedResult">): void {
    this.removeNode(node);
  }
}
